import {gql} from '@apollo/client'
import * as yup from 'yup'
import moment from 'moment'
import type {Status} from 'types/api/asyncTasks/asyncTask'

const supportedProcedureKinds = ['r1', 'r4', 'r6', 'r7', 'r57', 'r58', 'r60', 'r62'] as const

export type ProcedureKind = (typeof supportedProcedureKinds)[number]

const attachmentSchema = yup.object().shape({
  document_name: yup.string().required(),
  display_name: yup.string().required(),
  note: yup.string().optional(),
  file: yup.mixed<File>(),
})

export type Attachment = yup.InferType<typeof attachmentSchema>

export const schema = yup.object().shape({
  procedure_status_ids: yup.array(yup.string().required()).required(),
  procedure_kind: yup.string().oneOf(supportedProcedureKinds).required(),
  procedure_code: yup.string().required(),
  serial_number: yup
    .string()
    .defined()
    .test(
      'isSerialNumber',
      '001〜999までの数字を入力してください。',
      (value) => !!value && value !== '000' && /^\d{3}$/.test(value)
    ),
  submit_on: yup
    .string()
    .required('提出年月日を入力してください')
    .test(
      'dateFormat',
      () => '提出年月日の入力形式が正しくありません',
      (value) => {
        if (!value) return true
        return moment(value, 'YYYY/MM/DD').isValid() && /^\d{4}[/-]\d{1,2}[/-]\d{1,2}$/.test(value)
      }
    ),
  attachments: yup.array(attachmentSchema).required(),
})

export type PrepareCsvProcedureSchema = yup.InferType<typeof schema>

export const PREPARE_CSV_PROCEDURE = gql`
  mutation prepareCsvProcedure($input: PrepareCsvProcedureInput!) {
    prepareCsvProcedure(input: $input) {
      errors
    }
  }
`

const PREPARE_CSV_PROCEDURE_REQUEST_FIELDS_FRAGMENT = gql`
  fragment PrepareCsvProcedureRequestFields on PrepareCsvProcedureRequest {
    id
    actedUserId
    status
    startedAt
    finishedAt
    error
    confirmed
  }
`

export const ASYNC_PREPARE_CSV_PROCEDURE = gql`
  mutation asyncPrepareCsvProcedure($input: AsyncPrepareCsvProcedureInput!) {
    asyncPrepareCsvProcedure(input: $input) {
      prepareCsvProcedureRequest {
        ...PrepareCsvProcedureRequestFields
      }
    }
  }
  ${PREPARE_CSV_PROCEDURE_REQUEST_FIELDS_FRAGMENT}
`

export type QueryVariables = {
  procedureKind: string
}

export type RecommendedAttachment = {
  key: string
  displayName: string
  note?: string
}

export type QueryResult = {
  client: {
    id: string
    egovSerialNumber: {
      pensionOffice: string
      employmentInsurance: string
    }
    csvEgovInformation: {
      procedureCode: string
      recommendedAttachments: RecommendedAttachment[]
    }
  }
}

export const CSV_EGOV_INFORMATION = gql`
  query csvEgovInformation($procedureKind: String!) {
    client {
      id
      egovSerialNumber {
        pensionOffice
        employmentInsurance
      }
      csvEgovInformation(procedureKind: $procedureKind) {
        procedureCode
        recommendedAttachments {
          key
          displayName
          note
        }
      }
    }
  }
`

export interface PrepareCsvProcedureRequest {
  id: string
  actedUserId: string
  status: Status
  startedAt?: string
  finishedAt?: string
  error: string
  confirmed: boolean
}

export const PREPARE_CSV_PROCEDURE_REQUEST = gql`
  query prepareCsvProcedureRequest {
    client {
      id
      prepareCsvProcedureRequests {
        ...PrepareCsvProcedureRequestFields
      }
    }
  }
  ${PREPARE_CSV_PROCEDURE_REQUEST_FIELDS_FRAGMENT}
`

export const CONFIRM_PREPARE_CSV_PROCEDURE_REQUEST = gql`
  mutation confirmPrepareCsvProcedureRequest($id: ID!) {
    confirmPrepareCsvProcedureRequest(input: {id: $id}) {
      prepareCsvProcedureRequest {
        ...PrepareCsvProcedureRequestFields
      }
    }
  }
  ${PREPARE_CSV_PROCEDURE_REQUEST_FIELDS_FRAGMENT}
`
